import { useState, useEffect, useCallback } from "react";
import { TechnicalNote, TechnicalNoteStatus, TechnicalNotesListParams } from "types";
import { technicalNotesApi } from "api/models";
import { handleAxiosAndCaughtError } from "utils";
import Loader from "components/Loader";
import GenericTable from "components/table";
import { Dialog } from "components/dialog";
import {  Save, X, Check, Clock, Trash } from "lucide-react";
import toast from "react-hot-toast";
import { useConfirmation } from "components/dialog/confirmation";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { useSidebarCountsContext } from "contexts/SidebarCountsContext";
import TooltipHorizon from "components/tooltip";
import { useNotificationRefresh } from "contexts/NotificationContext";
import { useTranslation } from "react-i18next";

const TechnicalNotesTab = () => {
  const { t } = useTranslation();
  const { agentInfo } = useAgentInfo();
  const { refetchTechnicalNotesCount } = useSidebarCountsContext();
  const { triggerNotiRefresh } = useNotificationRefresh();
  const [technicalNotes, setTechnicalNotes] = useState<TechnicalNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedTechnicalNote, setSelectedTechnicalNote] = useState<TechnicalNote | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  const { confirm } = useConfirmation();

  const fetchTechnicalNotes = useCallback(async (signal?: AbortSignal) => {
    if (!agentInfo?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const params: TechnicalNotesListParams = {
        agent_id: agentInfo.id,
        take: 999,
        skip: 0,
      };
      
      const response = await technicalNotesApi.getList(params);
      setTechnicalNotes(response.items || []);
    } catch (err: any) {
      if (signal?.aborted || err.name === "CanceledError") {
        return;
      }
      setError("Click here to retry");
      setTechnicalNotes([]);
      handleAxiosAndCaughtError(err);
    } finally {
      setLoading(false);
    }
  }, [agentInfo?.id]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchTechnicalNotes(abortController.signal);

    return () => abortController.abort();
  }, [fetchTechnicalNotes]);

  const handleCreateTechnicalNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agentInfo?.id) {
      toast.error("Agent information not available");
      return;
    }
    
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    setIsCreating(true);
    try {
      await technicalNotesApi.create({
        question: formData.question.trim(),
        answer: formData.answer.trim(),
        agent_id: agentInfo.id,
      });
      
      toast.success("Technical note created successfully");
      setFormData({ question: "", answer: "" });
      setIsCreateDialogOpen(false);
      await fetchTechnicalNotes();
      await refetchTechnicalNotesCount();
      // Trigger immediate notification count refresh
      triggerNotiRefresh();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateTechnicalNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTechnicalNote || !formData.question.trim() || !formData.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    setIsUpdating(true);
    try {
      await technicalNotesApi.update(selectedTechnicalNote.id, {
        question: formData.question.trim(),
        answer: formData.answer.trim(),
      });
      
      toast.success("Technical note updated successfully");
      setFormData({ question: "", answer: "" });
      setIsEditDialogOpen(false);
      setSelectedTechnicalNote(null);
      await fetchTechnicalNotes();
      await refetchTechnicalNotesCount();
      // Trigger immediate notification count refresh
      triggerNotiRefresh();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteTechnicalNote = async (technicalNoteId: string) => {
    setUpdatingRowId(technicalNoteId);
    try {
      await technicalNotesApi.delete(technicalNoteId);
      await fetchTechnicalNotes();
      await refetchTechnicalNotesCount();
      // Trigger immediate notification count refresh
      triggerNotiRefresh();
      toast.success("Technical note deleted successfully");
      setUpdatingRowId(null);
    } catch (err) {
      setUpdatingRowId(null);
      handleAxiosAndCaughtError(err);
    }
  };

  const handleDeleteWithConfirmation = async (technicalNoteId: string, question: string) => {
    const confirmed = await confirm({
      title: `Delete technical note`,
      message: `Are you sure you want to delete this technical note?\n\n"${question}"\n\nThis action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      await handleDeleteTechnicalNote(technicalNoteId);
    }
  };

  const handleViewTechnicalNote = (technicalNote: TechnicalNote) => {
    setSelectedTechnicalNote(technicalNote);
    // Initialize form data with the selected note's data
    setFormData({
      question: technicalNote.question,
      answer: technicalNote.answer,
    });
    setIsViewDialogOpen(true);
  };

  const handleEditTechnicalNote = (technicalNote: TechnicalNote) => {
    setSelectedTechnicalNote(technicalNote);
    setFormData({
      question: technicalNote.question,
      answer: technicalNote.answer,
    });
    setIsEditDialogOpen(true);
  };

  const handleCancel = () => {
    setFormData({ question: "", answer: "" });
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedTechnicalNote(null);
  };

  const handleCancelEdit = () => {
    if (selectedTechnicalNote) {
      setFormData({
        question: selectedTechnicalNote.question,
        answer: selectedTechnicalNote.answer,
      });
    }
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedTechnicalNote(null);
    setFormData({ question: "", answer: "" });
  };

  const getStatusColor = (status: TechnicalNoteStatus) => {
    switch (status) {
      case TechnicalNoteStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case TechnicalNoteStatus.APPROVED:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case TechnicalNoteStatus.REJECTED:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getStatusIcon = (status: TechnicalNoteStatus) => {
    switch (status) {
      case TechnicalNoteStatus.PENDING:
        return <Clock size={12} />;
      case TechnicalNoteStatus.APPROVED:
        return <Check size={12} />;
      case TechnicalNoteStatus.REJECTED:
        return <X size={12} />;
      default:
        return <Clock size={12} />;
    }
  };

  // Define columns for GenericTable
  const columns = [
    {
      accessorKey: "question",
      header: () => <p>{t("library.question")}</p>,
      cell: (info: any) => (
        <p className="max-w-xs truncate text-sm text-navy-700 dark:text-white">
          {info.getValue() || t("library.noQuestionProvided")}
        </p>
      ),
    },
    {
      accessorKey: "answer",
      header: () => <p>{t("library.answer")}</p>,
      cell: (info: any) => (
        <p className="max-w-xs truncate text-sm text-navy-700 dark:text-white">
          {info.getValue() || t("library.noAnswerProvided")}
        </p>
      ),
    },
    {
      accessorKey: "status",
      header: () => <p>{t("library.status")}</p>,
      cell: (info: any) => {
        const status = info.getValue() as TechnicalNoteStatus;
        return (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(status)}`}>
            {getStatusIcon(status)}
            {status}
          </span>
        );
      },
      meta: {
        filterable: true,
      },
      filterFn: 'multiSelect',
    },
    {
      accessorKey: "actions",
      header: () => <p>{t("library.actions")}</p>,
      cell: (info: any) => {
        const technicalNote = info.row.original as TechnicalNote;
        return (
          <div className="flex items-center gap-4">
            {technicalNote.status === TechnicalNoteStatus.PENDING && (
              <TooltipHorizon
                trigger={
                  <button
                    disabled={updatingRowId === technicalNote.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteWithConfirmation(technicalNote.id, technicalNote.question);
                    }}
                    className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900"
                  >
                    <Trash size={16} />
                  </button>
                }
                content={<div>Remove technical note</div>}
                placement="top"
                extra=""
              />
            )}
          </div>
        );
      },
    },
  ];

  const dataWithActions = technicalNotes.map((row) => ({
    ...row,
    updatingRowId,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return updatingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  const handleRowClick = (technicalNote: TechnicalNote) => {
    handleViewTechnicalNote(technicalNote);
  };

  const handleSaveChanges = async () => {
    if (!selectedTechnicalNote || !formData.question.trim() || !formData.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    setIsUpdating(true);
    try {
      await technicalNotesApi.update(selectedTechnicalNote.id, {
        question: formData.question.trim(),
        answer: formData.answer.trim(),
      });
      
      toast.success("Technical note updated successfully");
      await fetchTechnicalNotes();
      // Update the selected note with new data
      setSelectedTechnicalNote({
        ...selectedTechnicalNote,
        question: formData.question.trim(),
        answer: formData.answer.trim(),
      });
      
      // Close the dialog after successful update
      setIsViewDialogOpen(false);
      setSelectedTechnicalNote(null);
      setFormData({ question: "", answer: "" });
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader txt={t("library.loadingTechnicalNotes")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="cursor-pointer text-red-500 hover:text-red-700"
          onClick={() => fetchTechnicalNotes()}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("library.searchTechnicalNotes")}
        debug={true}
        addLabel={t("library.addTechnicalNote")}
        renderRowClassName={renderRowClassName}
        onAdd={() => setIsCreateDialogOpen(true)}
        onRowClick={handleRowClick}
      />

      {/* View Details Dialog */}
      <Dialog 
        isOpen={isViewDialogOpen} 
        setIsOpen={setIsViewDialogOpen}
        header={t("library.technicalNoteDetails")}
        subHeader={t("library.viewTechnicalNoteInformation")}
      >
        {selectedTechnicalNote ? (
          <div className="max-h-[70vh] space-y-6 p-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("library.status")}
              </label>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(selectedTechnicalNote.status)}`}>
                  {getStatusIcon(selectedTechnicalNote.status)}
                  {selectedTechnicalNote.status}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("library.question")}
              </label>
              {selectedTechnicalNote.status === TechnicalNoteStatus.PENDING ? (
                <textarea
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              ) : (
                <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  {selectedTechnicalNote.question || t("library.noQuestionProvided")}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("library.answer")}
              </label>
              {selectedTechnicalNote.status === TechnicalNoteStatus.PENDING ? (
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  rows={4}
                />
              ) : (
                <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white whitespace-pre-wrap">
                  {selectedTechnicalNote.answer || t("library.noAnswerProvided")}
                </div>
              )}
            </div>

            {selectedTechnicalNote.status === TechnicalNoteStatus.PENDING && (
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={isUpdating || !formData.question.trim() || !formData.answer.trim()}
                  className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {isUpdating ? t("common.saving") : t("common.saveChanges")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500">
            {t("library.noTechnicalNoteSelected")}
          </div>
        )}
      </Dialog>

      {/* Create Dialog */}
      <Dialog 
        isOpen={isCreateDialogOpen} 
        setIsOpen={setIsCreateDialogOpen}
        header={t("library.createNewTechnicalNote")}
        subHeader={t("library.addTechnicalNoteToKnowledgeBase")}
      >
        <form onSubmit={handleCreateTechnicalNote} className="flex max-h-[70vh] flex-col gap-6 p-2">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("library.question")} *
              </label>
              <textarea
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder={t("library.enterQuestion")}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                rows={3}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("library.answer")} *
              </label>
              <textarea
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                placeholder={t("library.enterAnswer")}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                rows={4}
                required
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isCreating || !formData.question.trim() || !formData.answer.trim()}
              className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {isCreating ? t("common.creating") : t("library.createNote")}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        isOpen={isEditDialogOpen} 
        setIsOpen={setIsEditDialogOpen}
        header={t("library.editTechnicalNote")}
        subHeader={t("library.updateTechnicalNoteInformation")}
      >
        <form onSubmit={handleUpdateTechnicalNote} className="flex max-h-[70vh] flex-col gap-6 p-2">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("library.question")} *
              </label>
              <textarea
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder={t("library.enterQuestion")}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                rows={3}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("library.answer")} *
              </label>
              <textarea
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                placeholder={t("library.enterAnswer")}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                rows={4}
                required
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="submit"
              disabled={isUpdating || !formData.question.trim() || !formData.answer.trim()}
              className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {isUpdating ? t("common.updating") : t("library.updateNote")}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default TechnicalNotesTab;